import * as _ from 'underscore';

import { WebClientListResourceFetcher } from '../../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../../httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';
import { ListResult } from '../../../ts/fetchers/ListResult';
import { Filter } from '../../../ts/helpers/Filter';
import { ServerApiMapper } from '../../../ts/helpers/ServerModelHelper';
import { IServerUserInfo } from '../../../ts/models/IServerUserInfo';
import { Resource } from '../../../ts/resources/Resource';
import { IResourceQuery } from '../../../ts/resources/ResourceQuery';
import { CreateLocalServerUserRequest } from '../../../ts/ServerApi/requests/CreateLocalServerUserRequest';
import { CreateLocalServerUserWithoutPasswordRequest } from '../../../ts/ServerApi/requests/CreateLocalServerUserWithoutPasswordRequest';
import { DeleteServerUsersRequest } from '../../../ts/ServerApi/requests/DeleteServerUsersRequest';
import { DeleteUserConnectedDevicesRequest, IDeleteUserConnectedDevicesParams } from '../../../ts/ServerApi/requests/DeleteUserConnectedDevicesRequest';
import { DeleteUserRemoteAgentsRequest, IDeleteUserRemoteAgentsParams } from '../../../ts/ServerApi/requests/DeleteUserRemoteAgentsRequest';
import { ForcePasswordUpdateOnSiteUsersRequest } from '../../../ts/ServerApi/requests/ForcePasswordUpdateOnSiteUsers';
import { ForcePasswordUpdateRequest } from '../../../ts/ServerApi/requests/ForcePasswordUpdateRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../../../ts/ServerApi/requests/GeneratePublicKeyRequest';
import { GetPersonalAccessTokenClientIdsParams, GetPersonalAccessTokenClientIdsRequest, GetPersonalAccessTokenClientIdsResult } from '../../../ts/ServerApi/requests/GetPersonalAccessTokenClientIdsRequest';
import { GetServerUsersRequest } from '../../../ts/ServerApi/requests/GetServerUsersRequest';
import { GetUserConnectedDevicesRequest, IGetUserConnectedDevicesParams, IGetUserConnectedDevicesResult } from '../../../ts/ServerApi/requests/GetUserConnectedDevicesRequest';
import { GetUserRefreshTokenCountRequest, IGetUserRefreshTokenCountParams, IGetUserRefreshTokenCountResult } from '../../../ts/ServerApi/requests/GetUserRefreshTokenCountRequest';
import { GetUserSettingsRequest, IGetUserSettingsParams, IGetUserSettingsResult } from '../../../ts/ServerApi/requests/GetUserSettingsRequest';
import { GetUsersSiteRolesRequest, IGetUsersSiteRolesResult } from '../../../ts/ServerApi/requests/GetUsersSiteRolesRequest';
import { ImportServerUsersFromActiveDirectoryRequest } from '../../../ts/ServerApi/requests/ImportUsersFromActiveDirectoryRequest';
import { ResetLoginRateLimitingOnSiteUsersRequest } from '../../../ts/ServerApi/requests/ResetLoginRateLimitingOnSiteUsersRequest';
import { ResetLoginRateLimitingRequest } from '../../../ts/ServerApi/requests/ResetLoginRateLimitingRequest';
import { RevokePersonalAccessTokenByClientIdParams, RevokePersonalAccessTokenByClientIdRequest } from '../../../ts/ServerApi/requests/RevokePersonalAccessTokenByClientIdRequest';
import { UpdateServerUsersAdminStatusRequest } from '../../../ts/ServerApi/requests/UpdateServerUsersAdminStatusRequest';
import { IUpdateServerUsersSiteMembershipResult, UpdateServerUsersSiteMembershipRequest } from '../../../ts/ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IConnectedDevice } from '../../../ts/ServerApi/types/IConnectedDevice';
import { FilterFieldNames, IFieldFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { IPersonalAccessToken } from '../../../ts/ServerApi/types/IPersonalAccessToken';
import { ISiteRole } from '../../../ts/ServerApi/types/ISiteRole';
import { IUserActionResult } from '../../../ts/ServerApi/types/IUserActionResult';
import { NamedItemKeys } from '../../../ts/ServerApi/types/resourceParts';
import { SiteRolesResource } from './SiteRoles';

export class ServerUsers extends Resource<IServerUserInfo> {

  public static readonly instance = new ServerUsers(new WebClientListResourceFetcher(GetServerUsersRequest, ServerApiMapper.serverUsers));

  // returns the server user with the specified username & domainName.
  public getUserByUsernameAndDomain(username: string, domainName: string): Promise<IServerUserInfo> {
    return this.all()
      .addFilter(Filter.stringFieldEq('username', username))
      .addFilter(Filter.stringFieldEq('domainName', domainName))
      .getFirst();
  }

  // returns the server users with the specified IDs
  public getByUserIds(userIds: string[]): Promise<ListResult<IServerUserInfo>> {
    let query = this.all();
    const usersFilter = Filter.stringFieldInArray(NamedItemKeys.id, userIds);
    query = query.addFilter(usersFilter);

    return query.getCount().then((totalCount: number) => {
      return query.getSlice(0, totalCount);
    });
  }

  public getUserCountsByMaxSiteRole(baseQuery: IResourceQuery<IServerUserInfo>): Promise<_.Dictionary<number>> {

    // Remove any existing max site role filter
    const filtersWithoutMaxSiteRole = _.filter(baseQuery.getFilters(), (filter: IFieldFilterClause) => {
      return !(filter.field && filter.field === FilterFieldNames.maxSiteRole);
    });
    const modifiedQuery = baseQuery.withFilters(filtersWithoutMaxSiteRole);

    const counts: _.Dictionary<number> = {};

    const promises = _.map(SiteRolesResource.instance.optionsForCurrentUser(), (maxSiteRole: string) => {
      return modifiedQuery
        .addFilter(Filter.stringFieldEq(FilterFieldNames.maxSiteRole, maxSiteRole))
        .getCount()
        .then((count: number) => {
          counts[maxSiteRole] = count;
        });
    });

    // Any site role
    promises.push(modifiedQuery.getCount().then((count: number) => { counts['Any'] = count; }));

    return Promise.all(promises).then(() => counts);
  }

  public getUserSettings(username: string, domain: string): Promise<IGetUserSettingsResult> {
    const params: IGetUserSettingsParams = {
      username: username,
      domainName: domain
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetUserSettingsRequest(params));
  }

  public getUserRefreshTokenCount(username: string, domain: string): Promise<number> {
    const params: IGetUserRefreshTokenCountParams = {
      username: username,
      domainName: domain
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetUserRefreshTokenCountRequest(params))
      .then((result: IGetUserRefreshTokenCountResult) => result.refreshTokenCount);
  }

  public getUserConnectedDevices(userId: string): Promise<IConnectedDevice[]> {
    const params: IGetUserConnectedDevicesParams = {
      userId: userId
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetUserConnectedDevicesRequest(params))
      .then((result: IGetUserConnectedDevicesResult) => result.devices);
  }

  public deleteUserConnectedDevices(userId: string, deviceIds: string[]): Promise<IResult> {
    const params: IDeleteUserConnectedDevicesParams = {
      userId: userId,
      deviceIds: deviceIds
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new DeleteUserConnectedDevicesRequest(params));
  }

  public getPersonalAccessTokens(userId: string): Promise<IPersonalAccessToken[]> {
    const params: GetPersonalAccessTokenClientIdsParams = {
      userId: userId
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetPersonalAccessTokenClientIdsRequest(params))
      .then((result: GetPersonalAccessTokenClientIdsResult) => result.tokenInfos);
  }

  public deletePersonalAccessToken(userId: string, tokenName: string): Promise<IResult> {
    const params: RevokePersonalAccessTokenByClientIdParams = {
      userId: userId,
      tokenName: tokenName
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new RevokePersonalAccessTokenByClientIdRequest(params));
  }

  public deleteUserRemoteAgents(ownerId: string, deviceIds: string[]): Promise<IResult> {
    const params: IDeleteUserRemoteAgentsParams = {
      ownerId: ownerId,
      deviceIds: deviceIds
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new DeleteUserRemoteAgentsRequest(params));
  }

  // returns the server users' roles across all sites
  public getSiteRoles(userIds: string[]): Promise<IGetUsersSiteRolesResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new GetUsersSiteRolesRequest({ ids: userIdsBlock })),
      (results: IGetUsersSiteRolesResult[]) => {
        return {
          errors: ResultHelper.combineErrors(results),
          users: ResultHelper.combine(results, (result) => result.users)
        };
      });
  }

  // site membership - add/remove server users from sites
  public updateServerUsersSiteMembership(userIds: string[], addToSiteRoles: ISiteRole[], removeFromSiteIds: string[]): Promise<IUpdateServerUsersSiteMembershipResult>
  {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests3(userIds, addToSiteRoles, removeFromSiteIds,
        (userIdsBlock, addToSiteRolesBlock, removeFromSiteIdsBlock) => new UpdateServerUsersSiteMembershipRequest({
          userIds: userIdsBlock,
          addToSiteRoles: addToSiteRolesBlock,
          removeFromSiteIds: removeFromSiteIdsBlock
        })),
        (results: IUpdateServerUsersSiteMembershipResult[]) => {
          let res = ResultHelper.combine(results, (result) => result.results);
          let err = ResultHelper.combineErrors(results);
          return {
            results: res,
            errors: err ? err : []
          };
      });
  }

  // site membership - change these users' server admin status
  public updateServerAdminStatus(userIds: string[], admin: boolean): Promise<IResult>
  {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateServerUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  // force a password update on the users provided
  public forcePasswordUpdate(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ForcePasswordUpdateRequest({ systemUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the site users provided
  public forcePasswordUpdateOnSiteUsers(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ForcePasswordUpdateOnSiteUsersRequest({ siteUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the users provided
  public resetLoginRateLimiting(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ResetLoginRateLimitingRequest({ systemUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the site users provided
  public resetLoginRateLimitingOnSiteUsers(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ResetLoginRateLimitingOnSiteUsersRequest({ siteUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // delete users from server
  public deleteUsers(userIds: string[]): Promise<IUserActionResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new DeleteServerUsersRequest({ ids: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // import users from active directory to server
  public importUsersFromActiveDirectory(usernames: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(usernames,
        (usernamesBlock) => new ImportServerUsersFromActiveDirectoryRequest({usernames: usernamesBlock})),
      ResultHelper.combineImportUserResults);
  }

  // create a new local server user
  public createLocalServerUser(
    username: string,
    displayName: string,
    email: string,
    password: string): Promise<IResult> {

    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;
        const encryptedPassword = rsa.encrypt(password, key);
        const createLocalServerUserRequest = new CreateLocalServerUserRequest({
          username: username,
          displayName: displayName,
          email: email,
          encryptedPassword: encryptedPassword,
          keyId: keyId
        });
        return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(createLocalServerUserRequest);
      });
  }

    // create a new local server user when SAML or SiteSAML is enabled
  public createLocalServerUserWithoutPassword(
    username: string,
    displayName: string,
    email: string): Promise<IResult> {
        const createLocalServerRequest = new CreateLocalServerUserWithoutPasswordRequest({
          username: username,
          displayName: displayName,
          email: email
        });
        return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(createLocalServerRequest);
  }
}

export default ServerUsers.instance;
